import React from "react";
import { Grid, TextField, MenuItem, Tooltip, InputAdornment } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const SYSTEM_PARAMETER_ONE_ID = 1;

const SYSTEM_PARAMETER_ONE_TOOLTIP =
  "Preset: parameter 1 is fixed to Segment (Segment ID) for PLC integration.";

export interface EventParameter {
    parameter_id: number;
    parameter_contents: string;
    parameter_type: string;
}

export interface EventParametersProps {
    parameters: EventParameter[];
    isReader: boolean;
    isManufacturer: boolean;
    parameterTypes: string[];
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: number) => void;
}

const LockAdornment = React.memo(function LockAdornment() {
    return (
        <InputAdornment
            position="start"
            sx={{
                maxHeight: "none",
                marginRight: "2px",
                marginLeft: "-2px",
            }}
        >
            <Tooltip title={SYSTEM_PARAMETER_ONE_TOOLTIP} enterDelay={400} placement="top" arrow>
                <LockOutlinedIcon
                    fontSize="small"
                    color="action"
                    sx={{ opacity: 0.55, cursor: "help", display: "block" }}
                    aria-hidden
                />
            </Tooltip>
        </InputAdornment>
    );
});

export const EventParameters: React.FC<EventParametersProps> = React.memo(
    ({ parameters, isReader, isManufacturer, parameterTypes, onChange }) => {

        // Stabile MenuItems (sonst massives Re-Rendering)
        const menuItems = React.useMemo(() => {
            return parameterTypes.map((type) => (
                <MenuItem key={type} value={type}>
                    {type !== " " ? type : <em>None</em>}
                </MenuItem>
            ));
        }, [parameterTypes]);

        return (
            <Grid container spacing={2}>
                {parameters?.map((param) => {
                    const isSystemParameterOne = param.parameter_id === SYSTEM_PARAMETER_ONE_ID;
                    const roleLocked = isReader || isManufacturer;
                    const presetLocked = isSystemParameterOne && !roleLocked;

                    const contentsInputSlot = {
                        readOnly: roleLocked,
                        startAdornment: isSystemParameterOne ? <LockAdornment /> : undefined,
                        ...(isSystemParameterOne
                            ? {
                                  sx: {
                                      "& .MuiOutlinedInput-input": {
                                          paddingLeft: "8px",
                                      },
                                  },
                              }
                            : {}),
                    };
                    const typeInputSlot = {
                        readOnly: roleLocked,
                    };

                    return (
                    <React.Fragment key={param.parameter_id}>
                        {/* PARAMETER CONTENTS */}
                        <Grid size={{ xs: 5 }}>
                            <TextField
                                label="Parameter Contents"
                                name="parameter_contents"
                                value={param.parameter_contents}
                                onChange={(e) => onChange(e, param.parameter_id)}
                                fullWidth
                                disabled={presetLocked}
                                slotProps={{
                                    input: contentsInputSlot,
                                    inputLabel: { shrink: true },
                                }}
                                helperText={" "} // CLS-Vermeidung
                            />
                        </Grid>

                        {/* PARAMETER TYPE */}
                        <Grid size={{ xs: 3.5 }}>
                            <TextField
                                select
                                label="Parameter Type"
                                name="parameter_type"
                                value={param.parameter_type}
                                onChange={(e) => onChange(e, param.parameter_id)}
                                fullWidth
                                disabled={presetLocked}
                                slotProps={{
                                    input: typeInputSlot,
                                    inputLabel: { shrink: true },
                                }}
                                helperText={" "}
                            >
                                {menuItems}
                            </TextField>
                        </Grid>
                    </React.Fragment>
                    );
                })}
            </Grid>
        );
    }
);
